import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface UserBadge {
  id: string;
  badge_type: string;
  badge_name: string;
  badge_description: string | null;
  earned_at: string | null;
  is_active: boolean;
  image_url?: string | null;
}

// Official badge types - ONLY these 6 badges exist
export const OFFICIAL_BADGE_TYPES = ["early_creator", "rising_creator", "power_minter", "top_seller", "top_sharer", "most_liked"] as const;
export type OfficialBadgeType = typeof OFFICIAL_BADGE_TYPES[number];

// Badge images (stored in public/badges/)
export const BADGE_IMAGES: Record<OfficialBadgeType, string> = {
  early_creator: "/badges/early-creator.jpeg",
  rising_creator: "/badges/rising-creator.jpeg",
  power_minter: "/badges/power-minter.jpeg",
  top_seller: "/badges/top-seller.jpeg",
  top_sharer: "/badges/top-sharer.jpeg",
  most_liked: "/badges/most-liked.jpeg",
};

// Badge metadata
export const BADGE_METADATA: Record<OfficialBadgeType, { name: string; description: string }> = {
  early_creator: {
    name: "Early Creator",
    description: "One of the first creators on the platform",
  },
  rising_creator: {
    name: "Rising Creator",
    description: "Created 3+ approved NFTs",
  },
  power_minter: {
    name: "Power Minter",
    description: "Created 10+ NFTs on the platform",
  },
  top_seller: {
    name: "Top Seller",
    description: "Highest number of sales on the platform",
  },
  top_sharer: {
    name: "Top Sharer",
    description: "Most active sharer on the platform",
  },
  most_liked: {
    name: "Most Liked",
    description: "Most liked creator on the platform",
  },
};

export function useBadges(walletAddress?: string) {
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBadges = useCallback(async () => {
    setLoading(true);
    try {
      let data: UserBadge[] | null = null;
      let error = null;

      if (walletAddress) {
        // Fetch badges for specific wallet
        console.log("[useBadges] Fetching badges for wallet:", walletAddress);
        const result = await supabase
          .from("user_badges")
          .select("id, badge_type, badge_name, badge_description, earned_at, is_active, image_url")
          .eq("wallet_address", walletAddress)
          .eq("is_active", true)
          .eq("is_claimed", true)
          .in("badge_type", [...OFFICIAL_BADGE_TYPES])
          .order("earned_at", { ascending: true });

        data = result.data as UserBadge[] | null;
        error = result.error;
        console.log("[useBadges] Result for wallet:", { data, error });
      } else {
        // Fetch badges for current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log("[useBadges] No user found");
          setBadges([]);
          setLoading(false);
          return;
        }

        console.log("[useBadges] Fetching badges for user:", user.id);
        const result = await supabase
          .from("user_badges")
          .select("id, badge_type, badge_name, badge_description, earned_at, is_active, image_url")
          .eq("user_id", user.id)
          .eq("is_active", true)
          .eq("is_claimed", true)
          .in("badge_type", [...OFFICIAL_BADGE_TYPES])
          .order("earned_at", { ascending: true });

        data = result.data as UserBadge[] | null;
        error = result.error;
        console.log("[useBadges] Result for user:", { data, error });
      }

      if (error) {
        console.error("[useBadges] Error fetching badges:", error);
        setLoading(false);
        return;
      }

      // Only keep official badges
      const officialBadges = (data || []).filter(
        badge => OFFICIAL_BADGE_TYPES.includes(badge.badge_type as OfficialBadgeType)
      );

      console.log("[useBadges] Official badges found:", officialBadges.length);
      setBadges(officialBadges);
    } catch (err) {
      console.error("[useBadges] Badges error:", err);
    }
    setLoading(false);
  }, [walletAddress]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  return { badges, loading, refresh: fetchBadges };
}
